<script setup lang="ts">
import useCityStore from '~~/app/store/city'
import NetworkDataItem from '~~/app/components/Cities/NetworkDataItem.vue'
import { useBreakpoints } from '@vueuse/core'
import { getChargemapUrl } from '~~/app/utils/url'

const cdnUrl = useRuntimeConfig().app.cdnURL
const breakpoints = useBreakpoints({ tablet: 768 })
const isMobile = breakpoints.isSmallerOrEqual('tablet')

const cityStore = useCityStore()
const clickOnSeeDetails = ref(false)

const hasSomeNumbersToDisplay = computed<boolean>(() =>
    Boolean(
        cityStore.stationsCompatibleChargemapPassCount ||
            cityStore.stationsFreeCount ||
            cityStore.stationsFastChargeCount
    )
)
</script>

<template>
    <div
        class="mt-6 md:mt-0 px-4 md:py-9 bg-white rounded-2xl flex-col justify-center items-center gap-2 md:gap-6 inline-flex"
    >
        <div class="flex flex-col justify-center items-center gap-1">
            <h3>
                {{ $t('word.network') }}
            </h3>
            <NuxtImg
                class="w-9"
                :src="`${cdnUrl}/images/StationMapPin.svg`"
                :alt="$t('network')"
            />
            <div
                class="flex-col justify-start items-center gap-0.5 inline-flex"
            >
                <span
                    class="text-center text-neutral-900 text-5xl font-bold font-inter"
                >
                    {{ $n(cityStore.stationsTotalCount) }}
                </span>
                <span
                    class="text-center text-zinc-500 text-base font-normal font-inter leading-tight"
                >
                    {{ $t('cities.data.networks_info') }}
                </span>
            </div>
        </div>
        <div
            class="flex flex-col justify-start items-center gap-2 text-center pt-2 md:pt-0"
        >
            <div
                class="text-zinc-600 text-base font-bold font-inter leading-tight"
            >
                {{ $t('cities.data.top_networks_title') }}
            </div>
            <div class="flex gap-1">
                <NuxtLink
                    v-for="(network, index) in cityStore.topNetworks"
                    :key="network.id"
                    class="text-sky-700 hover:text-sky-500 text-base font-normal font-inter leading-tight"
                    :to="getChargemapUrl(`/networks/${network.slug}`)"
                    :title="network.name"
                >
                    {{ network.name }}{{ index === 2 ? '' : ',' }}
                </NuxtLink>
            </div>
        </div>
        <ChargemapB2CButton
            v-if="isMobile && hasSomeNumbersToDisplay"
            class="mt-3 mb-4"
            type="tertiary"
            size="large"
            @click="clickOnSeeDetails = !clickOnSeeDetails"
        >
            <template #icon>
                <IconChevronDown
                    class="size-6 transition-transform duration-300 transform-gpu"
                    :class="clickOnSeeDetails && 'rotate-180'"
                />
            </template>
            {{ $t('word.details') }}
        </ChargemapB2CButton>
        <div
            class="flex flex-col justify-start items-center gap-2.5 w-full border-t-1 border-zinc-300"
            :class="[!hasSomeNumbersToDisplay && 'mt-8 md:hidden']"
        >
            <div
                v-if="!isMobile || clickOnSeeDetails"
                class="flex flex-col md:flex-row justify-center mt-4 md:mt-2.5 gap-4 w-full"
            >
                <NetworkDataItem
                    v-if="cityStore.stationsCompatibleChargemapPassCount"
                    icon="IconChargingBadgePass"
                    :figure="cityStore.stationsCompatibleChargemapPassCount"
                    :label="$t('cities.data.stations.compatible')"
                />
                <NetworkDataItem
                    v-if="cityStore.stationsFreeCount"
                    icon="IconCoins"
                    :figure="cityStore.stationsFreeCount"
                    :label="$t('cities.data.stations.free')"
                />
                <NetworkDataItem
                    v-if="cityStore.stationsFastChargeCount"
                    icon="IconChargingStation"
                    :figure="cityStore.stationsFastChargeCount"
                    :label="$t('cities.data.stations.fast_charging')"
                />
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
h3 {
    @apply text-center text-xl md:text-2xl leading-tight md:leading-loose;
}
</style>
